/**
 * PeriodBar — FY + sub-period selector for report pages.
 * @file src/web/features/admin/components/PeriodBar.tsx
 *
 * Reads/writes the shared reporting-period context. Drop it at the top of
 * any report page; the page then reads `usePeriod().from/to` for the API.
 *
 * The financial YEAR is chosen in the sidebar, beside the entity — it is scope,
 * like the client is, and applies to every page at once. This bar carries only
 * the sub-period within that year, which is genuinely per-report.
 */

import { CalendarRange } from 'lucide-react';
import { usePeriod, MONTH_LABELS } from '../lib/period-context';

const SUBS: { value: string; label: string }[] = [
  { value: 'full', label: 'Full year' },
  { value: 'q1', label: 'Q1 · Apr–Jun' },
  { value: 'q2', label: 'Q2 · Jul–Sep' },
  { value: 'q3', label: 'Q3 · Oct–Dec' },
  { value: 'q4', label: 'Q4 · Jan–Mar' },
  ...MONTH_LABELS.map((m, i) => ({ value: `m${i}`, label: m })),
  { value: 'custom', label: 'Custom range' },
];

const selCls = 'cursor-pointer appearance-none rounded-lg border border-ink-200 bg-paper py-2 pl-3 pr-8 text-sm font-medium text-ink-800 focus:border-inkblack focus:outline-none focus:ring-2 focus:ring-highlighter';
const dateCls = 'rounded-lg border border-ink-200 bg-paper px-2.5 py-2 text-sm text-ink-800 focus:border-inkblack focus:outline-none focus:ring-2 focus:ring-highlighter';

export function PeriodBar() {
  const p = usePeriod();
  if (!p.ready) return null;
  if (p.fyOptions.length === 0) return null; // no data yet

  return (
    <div className="mb-5 flex flex-wrap items-center gap-2 rounded-xl border border-ink-200 bg-paper px-3 py-2.5">
      <span className="mr-1 inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-ink-400">
        <CalendarRange className="h-4 w-4" /> Period
      </span>
      <select value={p.sub} onChange={(e) => p.setSub(e.target.value)} className={selCls}>
        {SUBS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
      </select>
      {p.sub === 'custom' && (
        <div className="flex items-center gap-1.5">
          <input type="date" value={p.customFrom} onChange={(e) => p.setCustom(e.target.value, p.customTo)} className={dateCls} />
          <span className="text-ink-400">→</span>
          <input type="date" value={p.customTo} onChange={(e) => p.setCustom(p.customFrom, e.target.value)} className={dateCls} />
        </div>
      )}
      <span className="ml-auto hidden text-xs text-ink-400 sm:inline">{p.from} → {p.to}</span>
    </div>
  );
}
